import { LegalPageLayout } from '@/components/layout/LegalPageLayout';
import { LEGAL_SUITE_VERSION, LEGAL_SUITE_LAST_UPDATED_LABEL } from '@/constants/legalSuite';

export default function CookiesPolicy() {
  return (
    <LegalPageLayout
      title="Política de Cookies"
      lastUpdated={LEGAL_SUITE_LAST_UPDATED_LABEL}
      version={LEGAL_SUITE_VERSION}
    >
      <nav className="bg-muted/50 rounded-lg p-4 sm:p-6 mb-8 border">
        <p className="font-semibold text-foreground mb-3">Contenido</p>
        <ol className="!text-sm space-y-1">
          <li><a href="#que-son">Qué son las cookies</a></li>
          <li><a href="#tipos">Tipos de cookies que usamos</a></li>
          <li><a href="#necesarias">Cookies necesarias</a></li>
          <li><a href="#analiticas">Cookies analíticas</a></li>
          <li><a href="#funcionales">Cookies funcionales</a></li>
          <li><a href="#stripe-pago">Cookies durante el pago (Stripe)</a></li>
          <li><a href="#terceros">Cookies de terceros</a></li>
          <li><a href="#preferencias">Cómo cambiar preferencias</a></li>
          <li><a href="#deshabilitar">Cómo deshabilitar cookies desde el navegador</a></li>
        </ol>
      </nav>

      <p>
        Esta Política de Cookies explica cómo <strong>FOCO — Tu Dinero, Tu Poder</strong> utiliza cookies y
        tecnologías similares (como <code>localStorage</code>) cuando visitas o utilizas nuestra plataforma. Es
        aplicable a la app FOCO en <strong>tudinerotupoder.com</strong> y a cualquier sitio o app donde se utilicen
        cookies bajo la marca FOCO en México, Centroamérica y Costa Rica.
      </p>

      <h2 id="que-son">1. Qué son las cookies</h2>
      <p>
        Las cookies son pequeños archivos de texto que se almacenan en tu dispositivo (computadora, tableta
        o teléfono móvil) cuando visitas un sitio web. Se utilizan ampliamente para hacer que los sitios web
        funcionen de manera más eficiente y para proporcionar información a los propietarios del sitio.
      </p>
      <p>
        Adicionalmente, nuestra plataforma utiliza <strong>localStorage</strong>, una tecnología del navegador
        similar a las cookies; permite almacenar preferencias localmente en tu dispositivo.
      </p>
      <p className="text-sm text-muted-foreground">
        Para la <strong>sesión tras iniciar sesión</strong>, el sistema de autenticación guarda datos (incluidos
        tokens JWT) principalmente en <code className="text-xs">localStorage</code>, no solo en cookies con{' '}
        <strong>HttpOnly</strong>. Es el modelo habitual en esta arquitectura (SPA + Supabase). Las recomendaciones
        de cookies <strong>Secure</strong> / <strong>SameSite</strong> siguen aplicando a cookies que el sitio o
        terceros establezcan; la protección frente al abuso del token en el navegador incluye HTTPS, cabeceras de
        seguridad en el sitio y evitar XSS.
      </p>

      <h2 id="tipos">2. Tipos de cookies que usamos</h2>
      <p>Clasificamos las cookies y tecnologías de almacenamiento que utilizamos en las siguientes categorías:</p>

      <h2 id="necesarias">3. Cookies necesarias (esenciales)</h2>
      <p>
        Estas cookies son imprescindibles para el funcionamiento de la plataforma y no pueden ser desactivadas.
        Sin ellas, el servicio no puede funcionar correctamente.
      </p>
      <ul>
        <li>
          <strong>Autenticación de sesión:</strong> datos de sesión del proveedor de autenticación (incluidos tokens)
          principalmente en <code className="text-xs">localStorage</code>; el proveedor puede usar también cookies en
          su dominio o para OAuth. No es el mismo modelo que una única cookie de sesión solo HttpOnly.
        </li>
        <li>
          <strong>Seguridad:</strong> comunicación sobre HTTPS; cabeceras de seguridad en el sitio; mitigación de XSS;
          políticas de acceso en servidor. El modelo Bearer en API reduce el CSRF clásico frente a sesión solo por
          cookie.
        </li>
        <li><strong>Consentimiento de cookies:</strong> almacenamiento de tu decisión sobre la aceptación de cookies para no volver a preguntarte.</li>
      </ul>

      <h2 id="analiticas">4. Cookies analíticas</h2>
      <p>
        Estas cookies nos ayudan a entender cómo los usuarios interactúan con la plataforma, permitiéndonos
        mejorar la experiencia y las funcionalidades del servicio. Estas cookies solo se activan si das tu
        consentimiento explícito.
      </p>
      <ul>
        <li>Páginas más visitadas y tiempo de permanencia.</li>
        <li>Funcionalidades más utilizadas.</li>
        <li>Errores o problemas técnicos detectados.</li>
      </ul>
      <p>
        <strong>Nota:</strong> Actualmente, FOCO no utiliza servicios de analítica de terceros como Google Analytics.
        Si en el futuro se implementan, esta política será actualizada y se solicitará tu consentimiento antes de
        activarlas.
      </p>

      <h2 id="funcionales">5. Cookies funcionales</h2>
      <p>
        Permiten recordar tus preferencias para personalizar la experiencia. Estas cookies solo se activan con tu
        consentimiento.
      </p>
      <ul>
        <li><strong>Tema visual:</strong> preferencia de modo claro u oscuro (almacenado en localStorage).</li>
        <li><strong>Moneda predeterminada:</strong> la moneda seleccionada para mostrar tus finanzas.</li>
        <li><strong>Tema de gráficos:</strong> la paleta de colores seleccionada para las visualizaciones.</li>
      </ul>

      <h2 id="stripe-pago">6. Cookies durante el pago (Stripe)</h2>
      <p>
        Al contratar o gestionar <strong>Premium</strong>, la app te redirige a dominios de <strong>Stripe</strong>{' '}
        (Checkout o Portal de cliente). Allí Stripe puede usar cookies propias para el pago, la prevención de fraude y
        la sesión de checkout. Esas cookies se rigen por la{' '}
        <a
          href="https://stripe.com/privacy"
          className="text-primary underline hover:text-primary/80"
          target="_blank"
          rel="noopener noreferrer"
        >
          política de privacidad de Stripe
        </a>
        , no por el dominio tudinerotupoder.com mientras estés en Stripe. FOCO no integra actualmente scripts de pago
        de Stripe en nuestro dominio; el flujo es por redirección.
      </p>

      <h2 id="terceros">7. Cookies de terceros en nuestro dominio</h2>
      <p>
        En tudinerotupoder.com pueden intervenir cookies de terceros necesarios para el servicio:
      </p>
      <ul>
        <li>
          <strong>Sistema de autenticación:</strong> nuestro proveedor de infraestructura establece cookies esenciales
          para gestionar la autenticación segura de usuarios.
        </li>
        <li>
          <strong>Servicios de IA:</strong> las consultas al asistente financiero se procesan en servidor; no hay
          cookies de tracking publicitario por IA en nuestro dominio.
        </li>
      </ul>

      <h2 id="preferencias">8. Cómo cambiar preferencias</h2>
      <p>
        Puedes gestionar tus preferencias de cookies en cualquier momento desde la sección{' '}
        <strong>Configuración → Privacidad y Cookies</strong> dentro de tu cuenta en FOCO. Allí podrás activar o
        desactivar las cookies analíticas y funcionales según tu preferencia.
      </p>
      <p>
        Las cookies esenciales no pueden ser desactivadas ya que son necesarias para el funcionamiento
        básico de la plataforma.
      </p>

      <h2 id="deshabilitar">9. Cómo deshabilitar cookies desde el navegador</h2>
      <p>
        También puedes configurar tu navegador para bloquear o eliminar cookies. Ten en cuenta que esto podría afectar
        el funcionamiento de la plataforma. A continuación, los enlaces de instrucciones para los navegadores más
        comunes:
      </p>
      <ul>
        <li><strong>Google Chrome:</strong> Configuración → Privacidad y seguridad → Cookies</li>
        <li><strong>Mozilla Firefox:</strong> Opciones → Privacidad y seguridad → Cookies</li>
        <li><strong>Safari:</strong> Preferencias → Privacidad → Cookies</li>
        <li><strong>Microsoft Edge:</strong> Configuración → Cookies y permisos del sitio</li>
      </ul>
    </LegalPageLayout>
  );
}
